import { OverlayContainer } from '@angular/cdk/overlay';
import { AfterViewInit, Component, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog, MatMenuTrigger } from '@angular/material';
import { Subscription } from 'rxjs';

import {
    AxisFormat,
    AxisScalingType,
    DEFAULT_MENU_OPTIONS,
    IMenuOptions,
    SeriesDisplayMode
} from '../../models/menu-options';
import { EventsService } from '../../services/events.service';
import { MenuOptionsService } from '../../services/menu-options.service';
import { PlotsService } from '../../services/plots.service';
import { TimeRangeService } from '../../services/time-range';
import { UiOptionsService } from '../../services/ui-options.service';
import { RangeSelectorDialogComponent } from '../range-selector-dialog/range-selector-dialog.component';

/** Component for setting filters on the plot data */
@Component({
    selector: 'sci-menu-bar',
    templateUrl: './menu-bar.component.html',
    styles: [ `
        .top-level-menu-buttons button {
            z-index: 1050;
        }

        ::ng-deep.mat-menu-panel {
            max-width: none !important;
        }
    ` ]
})
export class MenuBarComponent implements AfterViewInit, OnDestroy {
    @ViewChild('plotsMenuTrigger', { static: true }) plotsMenuTrigger: MatMenuTrigger;
    @ViewChild('displayMenuTrigger', { static: true }) displayMenuTrigger: MatMenuTrigger;
    @ViewChild('timeRangeMenuTrigger', { static: true }) timeRangeMenuTrigger: MatMenuTrigger;
    @ViewChild('annotationsMenuTrigger', { static: true }) annotationsMenuTrigger: MatMenuTrigger;
    // register enums for use in our template
    AxisScalingType = AxisScalingType;
    AxisFormat = AxisFormat;
    SeriesDisplayMode = SeriesDisplayMode;

    openedTrigger: MatMenuTrigger;
    colorTheme: 'light' | 'dark' = 'light';
    crosshairSync: boolean;
    zoomSync: boolean;
    labelWidthSync: boolean;
    hasTimeHistory = false;
    cols = 1;
    rows = 1;
    menusOpen = false;
    globalMenuOptions: IMenuOptions = DEFAULT_MENU_OPTIONS;
    subscriptions: Subscription[] = [];
    events;

    constructor(
        private _plotsService: PlotsService,
        private _overlayContainer: OverlayContainer,
        private _timeRangeService: TimeRangeService,
        private _uiOptionsService: UiOptionsService,
        private _menuOptionsService: MenuOptionsService,
        private _eventsService: EventsService,
        private _dialog: MatDialog
    ) {
        this.crosshairSync = true;
        _plotsService.enableCrosshairSync();
        this.labelWidthSync = true;
        _plotsService.enableYAxisLabelSync();
    }

    @HostListener('click')
    toggleMenu() {
        // this allows us to open menus on hover after one menu is clicked
        this.menusOpen = ! this.menusOpen;
    }

    ngAfterViewInit() {
        this.globalMenuOptions = this._menuOptionsService.getGlobalMenuOptions();

        this._overlayContainer.getContainerElement().addEventListener('mouseup', () => {
            this.menusOpen = false;
            this.openedTrigger = null;
        });

        setTimeout( () => {
            this.subscriptions.push( this._eventsService.getAllEventTypes$().subscribe( newAllEvents => {
                this.events = newAllEvents;
                this.updateSelectedEvents();
            }));
        }, 0 );
    }

    ngOnDestroy() {
        this.subscriptions.forEach( subscription => subscription.unsubscribe() );
    }

    /** toggle synchronization of crosshairs across all plots */
    toggleCrosshairSync() {
        this.crosshairSync = !this.crosshairSync;
        this._plotsService.enableCrosshairSync( this.crosshairSync );
    }

    /** toggle synchronization of zooms across all plots */
    toggleZoomSync() {
        this.zoomSync = !this.zoomSync;
        this._plotsService.enableZoomSync( this.zoomSync );
    }

    /** toggle synchronization of the width of the y-axis labels. This makes the x-axis width consistent on all plots */
    toggleYAxisLabelSync() {
        this.labelWidthSync = !this.labelWidthSync;
        this._plotsService.enableYAxisLabelSync( this.labelWidthSync );
    }

    /** update the global color theme. Only 'light' and 'dark' are supported */
    changeColorTheme( newColorTheme: 'light' | 'dark' ) {
        this.colorTheme = newColorTheme;
        this._uiOptionsService.setDarkMode( newColorTheme );
    }

    /** change the number of rows and columns displayed */
    updateGrid( cols: number, rows: number ) {
        this.cols = cols;
        this.rows = rows;
        this._uiOptionsService.setPlotGrid( rows, cols );
    }

    /** if this menuTrigger isn't already open, open it and close the others */
    openMenu( menuTrigger: MatMenuTrigger ) {
        if ( this.menusOpen && this.openedTrigger !== menuTrigger ) {
            // close all menus, open this one
            this.plotsMenuTrigger.closeMenu();
            this.displayMenuTrigger.closeMenu();
            this.timeRangeMenuTrigger.closeMenu();
            this.annotationsMenuTrigger.closeMenu();
            menuTrigger.openMenu();
            this.openedTrigger = menuTrigger;
        }
    }

    updateSelectedEvents() {
        this.globalMenuOptions.view.eventTypes = this.events.filter( event => event.checked )
                                                            .map( event => event.id );
        this.updateGlobalOptions();
    }

    /** tell the menu options service to update the global options config and any associated plots */
    updateGlobalOptions() {
        this._menuOptionsService.setGlobalMenuOptions( this.globalMenuOptions );
    }

    /** returns true if all menus are closed */
    allMenusClosed(): boolean {
        return !this.plotsMenuTrigger.menuOpen
            && !this.displayMenuTrigger.menuOpen
            && !this.timeRangeMenuTrigger.menuOpen
            && !this.annotationsMenuTrigger.menuOpen;
    }

    /** if all menus are closed, set menusOpen to false */
    setMenusClosed() {
        if ( this.allMenusClosed() ) {
            this.menusOpen = false;
        }
    }

    // time range controls

    zoomOut() {
        this._timeRangeService.zoomOutAll();
    }

    zoomIn() {
        this._timeRangeService.zoomInAll();
    }

    panLeft() {
        this._timeRangeService.panLeftAll();
    }

    panRight() {
        this._timeRangeService.panRightAll();
    }

    undoZoom() {
        this._timeRangeService.undoGlobalTimeRangeChange();
    }

    setRangeByDuration( duration: number ) {
        this._timeRangeService.setAllRangesByDuration( duration );
    }

    openRangeSelectorModal() {
        const range = this._timeRangeService.getGlobalTimeRange().getValue();
        const dialogRef = this._dialog.open( RangeSelectorDialogComponent, {
            data: range
        });
        dialogRef.afterClosed().subscribe( newRange => {
            if ( newRange != null && newRange !== '' ) {
                this._timeRangeService.setAllPlotsTimeRange( newRange );
            }
        });
    }
}
